import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Cookie, Shield, Settings, Eye } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const CookiePolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
              Cookie Policy
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Learn about how we use cookies and similar technologies to enhance your FlameLink experience.
          </p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: December 15, 2024 | Last Updated: December 15, 2024</p>
        </div>

        <div className="space-y-8">
          {/* What are Cookies */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Cookie className="w-6 h-6 text-orange-500 mr-3" />
              1. What Are Cookies?
            </h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Cookies</strong> are small text files placed on your device when you visit websites or use apps. 
                They help websites remember your preferences, login status, and other information to improve your experience.
              </p>
              
              <p>
                <strong>Similar Technologies</strong> include local storage, session storage, pixels, web beacons, and 
                software development kits (SDKs) that serve similar purposes to cookies.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Why We Use Cookies</h3>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Keep you logged in to your account</li>
                  <li>• Remember your preferences and settings</li>
                  <li>• Analyze how you use FlameLink to improve our service</li>
                  <li>• Provide personalized matching recommendations</li>
                  <li>• Protect against fraud and ensure platform security</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Types of Cookies */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1 Essential Cookies (Always Active)</h3>
                <p className="text-gray-600 mb-3">
                  These cookies are necessary for FlameLink to function properly and cannot be disabled.
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Authentication:</strong> Keep you logged in and verify your identity</li>
                  <li>• <strong>Security:</strong> Protect against cross-site request forgery and other attacks</li>
                  <li>• <strong>Load Balancing:</strong> Distribute traffic across our servers</li>
                  <li>• <strong>Feature Functionality:</strong> Enable core app features like messaging and matching</li>
                </ul>
              </div>

              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.2 Performance & Analytics Cookies</h3>
                <p className="text-gray-600 mb-3">
                  Help us understand how users interact with FlameLink to improve performance and features.
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Google Analytics:</strong> Website and app usage statistics</li>
                  <li>• <strong>Performance Monitoring:</strong> App speed and error tracking</li>
                  <li>• <strong>A/B Testing:</strong> Compare different app versions and features</li>
                  <li>• <strong>User Journey:</strong> Understand how users navigate the app</li>
                </ul>
              </div>

              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.3 Functionality Cookies</h3>
                <p className="text-gray-600 mb-3">
                  Store your preferences and settings to provide a personalized experience.
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Language Preferences:</strong> Remember your chosen language</li>
                  <li>• <strong>Theme Settings:</strong> Dark mode, display preferences</li>
                  <li>• <strong>Location Settings:</strong> Remember your location for matching</li>
                  <li>• <strong>Filter Preferences:</strong> Save your matching criteria and filters</li>
                </ul>
              </div>

              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.4 Marketing & Advertising Cookies</h3>
                <p className="text-gray-600 mb-3">
                  Used to show you relevant advertisements and measure campaign effectiveness.
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• <strong>Facebook Pixel:</strong> Track conversions and show relevant ads</li>
                  <li>• <strong>Google Ads:</strong> Measure ad performance and retargeting</li>
                  <li>• <strong>Campaign Tracking:</strong> Understand which marketing channels work best</li>
                  <li>• <strong>Personalized Ads:</strong> Show relevant dating and lifestyle advertisements</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Third-Party Cookies */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Third-Party Cookies</h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                FlameLink uses third-party services that may set their own cookies on your device:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Analytics & Performance</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>Google Analytics:</strong> Website analytics</li>
                    <li>• <strong>Mixpanel:</strong> App usage analytics</li>
                    <li>• <strong>Sentry:</strong> Error tracking and monitoring</li>
                    <li>• <strong>Hotjar:</strong> User behavior analysis</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Advertising & Marketing</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>Facebook:</strong> Social media integration</li>
                    <li>• <strong>Google Ads:</strong> Advertising and remarketing</li>
                    <li>• <strong>Twitter:</strong> Social sharing and ads</li>
                    <li>• <strong>LinkedIn:</strong> Professional targeting</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Payment & Security</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>Stripe:</strong> Payment processing</li>
                    <li>• <strong>PayPal:</strong> Payment services</li>
                    <li>• <strong>Cloudflare:</strong> Security and performance</li>
                    <li>• <strong>reCAPTCHA:</strong> Spam and bot protection</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Communication</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>SendGrid:</strong> Email delivery</li>
                    <li>• <strong>Twilio:</strong> SMS notifications</li>
                    <li>• <strong>Zendesk:</strong> Customer support</li>
                    <li>• <strong>Intercom:</strong> Live chat support</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Cookie Management */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Settings className="w-6 h-6 text-blue-500 mr-3" />
              4. How to Manage Cookies
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 FlameLink Cookie Settings</h3>
                <p className="text-gray-700 mb-4">
                  You can control non-essential cookies through our cookie preference center:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-gray-900">Analytics Cookies</span>
                    <button className="bg-green-500 text-white px-3 py-1 rounded-full text-sm">Enabled</button>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-gray-900">Marketing Cookies</span>
                    <button className="bg-gray-400 text-white px-3 py-1 rounded-full text-sm">Disabled</button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">Functionality Cookies</span>
                    <button className="bg-green-500 text-white px-3 py-1 rounded-full text-sm">Enabled</button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Browser Settings</h3>
                <p className="text-gray-700 mb-4">
                  Most browsers allow you to control cookies through their settings:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm ml-4">
                  <li>• <strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                  <li>• <strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                  <li>• <strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li>• <strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.3 Mobile App Settings</h3>
                <p className="text-gray-700 mb-4">
                  For mobile apps, you can control tracking through device settings:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm ml-4">
                  <li>• <strong>iOS:</strong> Settings → Privacy & Security → Tracking → Allow Apps to Request to Track</li>
                  <li>• <strong>Android:</strong> Settings → Privacy → Ads → Delete advertising ID</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Important Note</p>
                    <p>
                      Disabling essential cookies will prevent FlameLink from functioning properly. 
                      You may not be able to log in, send messages, or use key features if these cookies are blocked.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Data Retention */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Cookie Data Retention</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Retention Periods</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>
                    <strong>Session Cookies:</strong> Deleted when you close your browser or app
                  </li>
                  <li>
                    <strong>Persistent Cookies:</strong> Remain until expiration date or manual deletion
                    <ul className="mt-2 ml-6 text-sm space-y-1">
                      <li>• Authentication cookies: 30 days</li>
                      <li>• Preference cookies: 1 year</li>
                      <li>• Analytics cookies: 2 years</li>
                      <li>• Marketing cookies: 30-90 days (varies by provider)</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2 Automatic Deletion</h3>
                <p className="text-gray-700">
                  We regularly review and delete unnecessary cookie data. Expired cookies are automatically 
                  removed, and we limit data collection to what's necessary for our legitimate purposes.
                </p>
              </div>
            </div>
          </Card>

          {/* Legal Compliance */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Legal Compliance</h2>
            
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.1 GDPR Compliance (EU Users)</h3>
                <ul className="space-y-2 ml-4">
                  <li>• We obtain consent for non-essential cookies</li>
                  <li>• You can withdraw consent at any time</li>
                  <li>• We provide clear information about our cookie usage</li>
                  <li>• We respect your right to object to processing</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.2 CCPA Compliance (California Users)</h3>
                <ul className="space-y-2 ml-4">
                  <li>• We disclose categories of personal information collected via cookies</li>
                  <li>• You can opt-out of the "sale" of personal information</li>
                  <li>• We provide methods to delete personal information</li>
                  <li>• We don't discriminate against users who exercise their rights</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6.3 Other Jurisdictions</h3>
                <p>
                  We comply with applicable cookie and privacy laws in all jurisdictions where FlameLink operates, 
                  including PIPEDA (Canada), LGPD (Brazil), and other regional regulations.
                </p>
              </div>
            </div>
          </Card>

          {/* Updates to Policy */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Updates to This Policy</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices, 
                technologies, legal requirements, or business operations.
              </p>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">7.1 How We Notify You</h3>
                <ul className="space-y-2 ml-4">
                  <li>• In-app notifications for significant changes</li>
                  <li>• Email updates to registered users</li>
                  <li>• Updated "Last Modified" date on this page</li>
                  <li>• Banner notifications on our website</li>
                </ul>
              </div>

              <p>
                <strong>Continued Use:</strong> Your continued use of FlameLink after policy updates 
                constitutes acceptance of the revised Cookie Policy.
              </p>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Our Cookie Policy?</h3>
            <p className="text-gray-600 mb-6">
              Contact our privacy team for any questions about cookies or data processing
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open('mailto:privacy@flamelink.com', '_blank')}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              >
                Contact Privacy Team
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;